import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ChannelLabel } from "@sequentech/ui-essentials";
import { ElectionHeaderStyles } from "@/components/styles/ElectionHeaderStyles";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { useMessagingAccounts } from "@/hooks/useMessagingAccounts";
import { roleContext, useMessagingReadRole } from "@/hooks/useMessagingReadRole";
import { GET_VOTER_MESSAGE_ATTEMPTS, GET_VOTER_UNSETTLED_MESSAGES, } from "@/queries/GetLedgerMessages";
import { EMessageChannel, EMessagingProvider } from "@/types/messaging";
import { maskDestination } from "@/services/maskDestination";
import { reasonText } from "@/services/messagingReasons";
import { ledgerStatus } from "@/components/logs/LedgerStatus";
import { voterDeliveryProblems, voterMessaging } from "./voterMessaging";
var Row = function (_a) {
    var label = _a.label, help = _a.help, children = _a.children;
    return (_jsxs(Box, { sx: { display: "flex", gap: 2, alignItems: "center", minHeight: 32, flexWrap: "wrap" }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", sx: { minWidth: 180 }, title: help, children: label }), _jsx(Box, { sx: { display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }, children: children })] }));
};
var formatTime = function (iso, language) {
    return iso ? new Date(iso).toLocaleString(language) : "";
};
/**
 * The voter's one channel, as Keycloak verified it: the channel, the masked
 * number or the Messenger Page, the consent time, the last message that
 * failed, and apart from it the last one whose delivery isn't confirmed
 * (never shown as a failure). Read only.
 */
export var VoterMessagingSummary = function (_a) {
    var _b;
    var attributes = _a.attributes, email = _a.email, userId = _a.userId, electionEventId = _a.electionEventId;
    var _c = useTranslation(), t = _c.t, i18n = _c.i18n;
    var tenantId = useTenantStore()[0];
    var role = useMessagingReadRole();
    var _d = voterMessaging(email, attributes), channel = _d.channel, consentAt = _d.consentAt;
    var isMessenger = (channel === null || channel === void 0 ? void 0 : channel.channel) === EMessageChannel.MESSENGER;
    var accounts = useMessagingAccounts({ skip: !isMessenger, summaries: true }).accounts;
    var _e = useQuery(GET_VOTER_UNSETTLED_MESSAGES, {
        variables: { tenantId: tenantId, electionEventId: electionEventId, voterId: userId },
        skip: !role || !tenantId || !electionEventId || !userId,
        context: roleContext(role),
    }), data = _e.data, loading = _e.loading;
    var candidates = useMemo(function () { var _a; return (_a = data === null || data === void 0 ? void 0 : data.sequent_backend_message) !== null && _a !== void 0 ? _a : []; }, [data]);
    var keys = useMemo(function () {
        return Array.from(new Set(candidates
            .map(function (message) { return message.logical_key; })
            .filter(function (key) { return !!key; }))).sort();
    }, [candidates]);
    var _f = useQuery(GET_VOTER_MESSAGE_ATTEMPTS, {
        variables: { tenantId: tenantId, keys: keys },
        skip: !role || !tenantId || keys.length === 0,
        context: roleContext(role),
    }), attemptsData = _f.data, attemptsLoading = _f.loading;
    var _g = useMemo(function () { var _a; return voterDeliveryProblems(candidates, (_a = attemptsData === null || attemptsData === void 0 ? void 0 : attemptsData.sequent_backend_message) !== null && _a !== void 0 ? _a : []); }, [candidates, attemptsData]), failure = _g.failure, unconfirmed = _g.unconfirmed;
    var notSet = t("messaging.voter.notSet");
    var page = isMessenger
        ? accounts
            .map(function (account) { return account.sender; })
            .find(function (sender) {
            return sender.provider === EMessagingProvider.MESSENGER_SEND_API &&
                sender.page_id === (channel === null || channel === void 0 ? void 0 : channel.messengerPage);
        })
        : undefined;
    var pageName = (page === null || page === void 0 ? void 0 : page.provider) === EMessagingProvider.MESSENGER_SEND_API ? page.page_name : undefined;
    var messageText = function (message) {
        var _a;
        if (!role)
            return t("messaging.voter.failureNeedsPermission");
        if (loading || attemptsLoading)
            return "–";
        if (!message)
            return t("messaging.voter.noFailure");
        var status = ledgerStatus(message.state, message.created_at);
        return [
            status ? t("messaging.state.".concat(status)) : message.state,
            formatTime((_a = message.failed_at) !== null && _a !== void 0 ? _a : message.updated_at, i18n.language),
            reasonText(t, message.error),
        ]
            .filter(Boolean)
            .join(" · ");
    };
    return (_jsxs(Box, { component: "section", "aria-label": String(t("messaging.voter.title")), sx: { display: "flex", flexDirection: "column", gap: 0.5, margin: "16px 0" }, children: [_jsx(ElectionHeaderStyles.Title, { children: t("messaging.voter.title") }), _jsx(Row, { label: t("messaging.voter.channel"), children: channel ? (_jsx(ChannelLabel, { channel: channel.channel, label: t("messaging.channel.".concat(channel.channel)) })) : (_jsx(Typography, { variant: "body2", children: t("messaging.voter.noChannel") })) }), channel ? (_jsx(Row, { label: isMessenger
                    ? t("messaging.voter.messengerPage")
                    : t("messaging.voter.destination"), children: _jsx(Typography, { variant: "body2", children: isMessenger
                        ? (pageName !== null && pageName !== void 0 ? pageName : channel.messengerPage)
                        : ((_b = maskDestination(channel.channel, channel.destination)) !== null && _b !== void 0 ? _b : 
                        // The backend refuses it: shown as the reason a send would give.
                        t("messaging.reason.INVALID_DESTINATION")) }) })) : null, _jsx(Row, { label: t("messaging.voter.consent"), children: _jsx(Typography, { variant: "body2", children: consentAt ? formatTime(consentAt, i18n.language) : notSet }) }), _jsx(Row, { label: t("messaging.voter.lastFailure"), children: _jsx(Typography, { variant: "body2", children: messageText(failure) }) }), _jsx(Row, { label: t("messaging.voter.notConfirmed"), help: t("messaging.voter.notConfirmedHelp"), children: _jsx(Typography, { variant: "body2", children: messageText(unconfirmed) }) })] }));
};
